/**
 * WARNING:
 * This component MAY receive an optional caseId.
 * Page-level components MUST NOT copy this pattern.
 * All pages MUST call requireCaseId().
 */
import { NavLink, useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Users,
  Search,
  Sparkles,
  Hammer,
  Clock,
  FileText,
  BookOpen,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  FolderOpen,
  History,
  X,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useConnectionRequestCount } from '@/hooks/useConnectionRequestCount';
import { PHASE_ICONS } from '@/config/phaseIcons';

interface NavItem {
  name: string;
  icon: typeof FileText;
  path: string;
  end?: boolean;
  badge?: number;
}

interface SidebarLinkProps {
  item: NavItem;
  isCollapsed: boolean;
}

function SidebarLink({ item, isCollapsed }: SidebarLinkProps) {
  const Icon = item.icon;
  const hasBadge = item.badge !== undefined && item.badge > 0;
  
  return (
    <NavLink
      to={item.path}
      end={item.end}
      className={({ isActive }) =>
        `relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
          isActive
            ? 'bg-slate-800 text-white'
            : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
        } ${isCollapsed ? 'justify-center' : ''}`
      }
      title={isCollapsed ? item.name : undefined}
    >
      <Icon size={18} className="flex-shrink-0" />
      {!isCollapsed && (
        <span className="flex-1">{item.name}</span>
      )}
      {hasBadge && !isCollapsed && (
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-purple-600 px-1.5 text-xs font-semibold text-white">
          {item.badge}
        </span>
      )}
      {hasBadge && isCollapsed && (
        <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-purple-600"></span>
      )}
    </NavLink>
  );
}

interface AccordionSectionProps {
  label: string;
  icon: typeof FileText;
  rootPath: string;
  children: NavItem[];
  isCollapsed: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

function AccordionSection({ label, icon: Icon, rootPath, children, isCollapsed, isExpanded, onToggle }: AccordionSectionProps) {
  const location = useLocation();
  const isChildActive = children.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'));
  const isRootActive = location.pathname === rootPath;
  const isActive = isRootActive || isChildActive;

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className={`relative w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
          isActive ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
        } ${isCollapsed ? 'justify-center' : ''}`}
        title={isCollapsed ? label : undefined}
      >
        <Icon size={18} className="flex-shrink-0" />
        {!isCollapsed && (
          <>
            <span className="flex-1 text-left">{label}</span>
            {isExpanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
          </>
        )}
      </button>
      {isExpanded && !isCollapsed && (
        <div className="mt-1 ml-4 space-y-0.5 border-l border-slate-700/60 pl-3">
          {children.map((item) => (
            <SidebarLink key={item.path} item={item} isCollapsed={false} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PrimarySidebar({
  mobileOpen = false,
  onMobileClose,
}: {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}) {
  const { caseId } = useParams<{ caseId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [buildCaseExpanded, setBuildCaseExpanded] = useState(false);
  const [docsCenterExpanded, setDocsCenterExpanded] = useState(false);
  const [connectionsExpanded, setConnectionsExpanded] = useState(false);
  const { count: connectionRequestCount } = useConnectionRequestCount(caseId);

  // Auto-expand accordion when user is on a child route
  useEffect(() => {
    if (!caseId) return;
    const path = location.pathname;
    const buildCasePaths = [`/cases/${caseId}/journal`, `/cases/${caseId}/timeline`, `/cases/${caseId}/exhibits`, `/cases/${caseId}/inbox`];
    const docsCenterPaths = [`/cases/${caseId}/drafts`, `/cases/${caseId}/forms`, `/cases/${caseId}/filings`, `/cases/${caseId}/discovery`, `/cases/${caseId}/exports`];
    const connectionsPaths = [`/cases/${caseId}/members`, `/cases/${caseId}/share`];
    if (buildCasePaths.some(p => path.startsWith(p))) setBuildCaseExpanded(true);
    if (docsCenterPaths.some(p => path.startsWith(p))) setDocsCenterExpanded(true);
    if (connectionsPaths.some(p => path.startsWith(p))) setConnectionsExpanded(true);
  }, [caseId, location.pathname]);

  // Global workspace navigation — Settings removed (WO-03: moved to user dropdown only)
  const workspaceItems: NavItem[] = [
    { name: 'Research Hub', icon: Sparkles, path: '/help' },
    { name: 'Legal Info', icon: ShieldCheck, path: '/legal-disclaimer' },
  ];

  // Build Case sub-nav items (WO-04)
  const buildCaseChildren: NavItem[] = caseId ? [
    { name: 'Journal',          icon: PHASE_ICONS.journal,   path: `/cases/${caseId}/journal` },
    { name: 'Timeline Builder', icon: Clock,                  path: `/cases/${caseId}/timeline` },
    { name: 'Exhibits',         icon: PHASE_ICONS.exhibits,   path: `/cases/${caseId}/exhibits` },
    { name: 'Inbox',            icon: PHASE_ICONS.inbox,      path: `/cases/${caseId}/inbox` },
  ] : [];

  // Documents Center sub-nav items (WO-04)
  const docsCenterChildren: NavItem[] = caseId ? [
    { name: 'Drafts',     icon: PHASE_ICONS.drafts,     path: `/cases/${caseId}/drafts` },
    { name: 'Forms',      icon: PHASE_ICONS.forms,      path: `/cases/${caseId}/forms` },
    { name: 'File',       icon: FileText,               path: `/cases/${caseId}/filings` },
    { name: 'Discovery',  icon: PHASE_ICONS.discovery,  path: `/cases/${caseId}/discovery` },
    { name: 'Exports',    icon: PHASE_ICONS.exports,    path: `/cases/${caseId}/exports` },
  ] : [];

  // Remaining standalone case-workspace items (WO-04 order)
  // IMPORTANT:
  //   - AI Suggestions is NOT a standalone nav item — it is integrated throughout the app
  //   - Notifications is NOT a nav item — accessible via top-bar bell icon only
  //   - Connections expands to reveal Share (accordion). They are NOT separate top-level nav items.
  const connectionsChildren: NavItem[] = caseId ? [
    { name: 'Share', icon: PHASE_ICONS.share, path: `/cases/${caseId}/share` },
  ] : [];

  const standaloneItems: NavItem[] = caseId ? [
    { name: 'Case Guides',    icon: BookOpen,    path: `/cases/${caseId}/guides` },
    { name: 'Calendar',       icon: CalendarDays, path: `/cases/${caseId}/calendar` },
    { name: 'SmartBox™',       icon: Sparkles,    path: `/cases/${caseId}/key-details` },
    { name: 'Case History',   icon: History,     path: `/cases/${caseId}/chronology` },
    { name: 'Find Attorneys', icon: Search,      path: '/marketplace/directory' },
  ] : [];

  const sidebarWidth = isCollapsed ? 'w-[68px]' : 'w-56';

  // Shared nav content rendered in both desktop sidebar and mobile drawer
  const navContent = (collapsed: boolean, onNavClick?: () => void) => (
    <nav data-testid="primary-sidebar" aria-label="Primary workspace navigation" className="flex-1 overflow-y-auto p-3" onClick={onNavClick}>
      {/* Workspace Section */}
      <div className="mb-6">
        {!collapsed && (
          <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Workspace
          </div>
        )}
        <div className="space-y-1">
          {workspaceItems.map((item) => (
            <SidebarLink key={item.path} item={item} isCollapsed={collapsed} />
          ))}
        </div>
      </div>

      {/* Case Workspace Section */}
      {caseId && (
        <div>
          {!collapsed && (
            <div className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Case Workspace
            </div>
          )}
          <div className="space-y-1">
            {/* Build Case -- expandable accordion (WO-04) */}
            <AccordionSection
              label="Build Case"
              icon={Hammer}
              rootPath={`/cases/${caseId}`}
              children={buildCaseChildren}
              isCollapsed={collapsed}
              isExpanded={buildCaseExpanded}
              onToggle={() => setBuildCaseExpanded((v) => !v)}
            />

            {/* Documents Center -- expandable accordion (WO-04) */}
            <AccordionSection
              label="Documents Center"
              icon={FolderOpen}
              rootPath={`/cases/${caseId}/documents`}
              children={docsCenterChildren}
              isCollapsed={collapsed}
              isExpanded={docsCenterExpanded}
              onToggle={() => setDocsCenterExpanded((v) => !v)}
            />

            {/* Standalone items */}
            {standaloneItems.map((item) => (
              <SidebarLink key={item.path} item={item} isCollapsed={collapsed} />
            ))}

            {/* Connections -- accordion; Share is a child, NOT a separate top-level item */}
            <AccordionSection
              label="Connections"
              icon={Users}
              rootPath={`/cases/${caseId}/members`}
              children={connectionsChildren}
              isCollapsed={collapsed}
              isExpanded={connectionsExpanded}
              onToggle={() => {
                setConnectionsExpanded((v) => !v);
                if (caseId) navigate(`/cases/${caseId}/share`);
              }}
            />
          </div>
        </div>
      )}
    </nav>
  );

  return (
    <>
      {/* DESKTOP sidebar -- hidden on mobile */}
      <aside className={`hidden lg:flex flex-col ${sidebarWidth} relative border-r border-slate-700/50 bg-slate-900/50 backdrop-blur-sm text-white transition-all duration-200`}>
        {/* Collapse/Expand Toggle */}
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-20 z-10 rounded-full border border-slate-700 bg-slate-800 p-1 text-slate-400 shadow-md transition hover:bg-slate-700"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>

        {navContent(isCollapsed)}
      </aside>

      {/* MOBILE drawer -- slides in from left when mobileOpen=true */}
      <div
        className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-200 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!mobileOpen}
        role="dialog"
        aria-label="Navigation menu"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onMobileClose}
        />

        {/* Drawer panel */}
        <aside
          className={`absolute left-0 top-0 h-full w-72 max-w-[85vw] flex flex-col bg-slate-900 border-r border-slate-700/50 text-white transition-transform duration-200 ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Drawer header with close button */}
          <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3 flex-shrink-0">
            <span className="text-sm font-semibold text-slate-300">Navigation</span>
            <button
              type="button"
              onClick={onMobileClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white transition"
              aria-label="Close navigation"
            >
              <X size={18} />
            </button>
          </div>

          {/* Same nav content, never collapsed in drawer */}
          {navContent(false, onMobileClose)}
        </aside>
      </div>
    </>
  );
}
